import { useEffect, useState } from "react";
import { Footer } from "./components/Footer";
import { TooltipRoot } from "./components/Tooltip";
import { Wordmark } from "./components/Wordmark";
import { DashboardLayout } from "./pages/DashboardLayout";
import { DashboardPage } from "./pages/DashboardPage";
import { UsagePage } from "./pages/UsagePage";
import { ApiKeysPage } from "./pages/ApiKeysPage";
import { RegistrationsPage } from "./pages/RegistrationsPage";
import { TemplatesPage } from "./pages/TemplatesPage";
import { ReceiptsPage } from "./pages/ReceiptsPage";
import { ReceiptDetailPage } from "./pages/ReceiptDetailPage";
import { WitnessLogPage } from "./pages/WitnessLogPage";
import { WitnessLogDetailPage } from "./pages/WitnessLogDetailPage";
import { BillingPage } from "./pages/BillingPage";
import { SettingsPage } from "./pages/SettingsPage";
import { UpgradePage } from "./pages/UpgradePage";
import {
  isAuthPath,
  parseReceiptDetailId,
  parseWitnessLogDetailPosition,
  pathToSection,
  receiptDetailPath,
  sectionToPath,
  witnessLogDetailPath,
  type DashboardSection
} from "./routes";
import {
  api,
  ApiError,
  clearSessionToken,
  getSessionToken,
  setSessionToken,
  type AuthCallbackResponse
} from "./api/client";
import { clearPendingRegistration } from "./lib/pending-registration";

type View =
  | { kind: "loading" }
  | { kind: "landing" }
  | { kind: "exchanging" }
  | { kind: "exchange_failed"; reason: string }
  | {
      kind: "dashboard";
      email: string | null;
      tier: string | null;
      section: DashboardSection;
      // When the URL is /receipts/<id> the receipts section renders the
      // detail page instead of the list. Stored here so popstate and
      // sidebar navigation can transition the view without unmounting
      // the layout chrome.
      receiptDetailId?: string;
      // Witness log mirrors the same pattern with a positive integer
      // position addressing each entry. Kept separate from
      // receiptDetailId rather than unified so the two detail kinds
      // never conflate.
      witnessLogDetailPosition?: number;
      issuedKeys?: { api_key: string; read_only_api_key: string };
      upgradeNotice?: "success" | "cancelled";
    };

const VITE_ENV = (import.meta as unknown as { env?: Record<string, string> }).env ?? {};
const WORKOS_CLIENT_ID = VITE_ENV.VITE_WORKOS_CLIENT_ID ?? "";
const WORKOS_API_HOSTNAME = VITE_ENV.VITE_WORKOS_API_HOSTNAME ?? "api.workos.com";

// sessionStorage key holding the in-flight auth transaction (the OAuth
// state and the PKCE code_verifier). Single-use: the callback reads it
// once and removes it immediately so a code cannot be replayed.
const AUTH_TXN_KEY = "sequesign:auth:transaction";

// base64url without padding, per RFC 7636.
function base64UrlEncode(bytes: Uint8Array): string {
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replaceAll("=", "");
}

function randomBase64Url(byteLength: number): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

async function sha256Base64Url(input: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(input));
  return base64UrlEncode(new Uint8Array(digest));
}

// Constant-time string compare for the OAuth state check. Length-prefixed
// XOR fold: it folds the length difference into the accumulator and still
// walks the longer string on a length mismatch (returning false) rather
// than short-circuiting, so the comparison time does not leak how long a
// matching prefix was or what length the expected value is.
function constantTimeEqual(a: string, b: string): boolean {
  let diff = a.length ^ b.length;
  const max = Math.max(a.length, b.length);
  for (let i = 0; i < max; i++) {
    diff |= (a.charCodeAt(i) || 0) ^ (b.charCodeAt(i) || 0);
  }
  return diff === 0;
}

// Begin an AuthKit sign-in or sign-up. Generates an OAuth `state` and a
// PKCE `code_verifier`, records both in sessionStorage for the callback
// to validate, and redirects to AuthKit's hosted UI carrying the matching
// `state` and the `code_challenge` (the SHA-256 of the verifier). PKCE is
// what closes the login-CSRF / session-fixation gap at the WorkOS
// exchange; the state check is defense in depth on the frontend.
async function beginAuth(screenHint: "sign-in" | "sign-up"): Promise<void> {
  const state = randomBase64Url(32); // 43 chars
  const codeVerifier = randomBase64Url(48); // 64 chars, within RFC 7636 43-128
  const codeChallenge = await sha256Base64Url(codeVerifier);
  sessionStorage.setItem(AUTH_TXN_KEY, JSON.stringify({ state, code_verifier: codeVerifier }));
  const redirectUri = `${window.location.origin}/auth/callback`;
  const params = new URLSearchParams({
    response_type: "code",
    client_id: WORKOS_CLIENT_ID,
    redirect_uri: redirectUri,
    provider: "authkit",
    screen_hint: screenHint,
    state,
    code_challenge: codeChallenge,
    code_challenge_method: "S256"
  });
  window.location.assign(
    `https://${WORKOS_API_HOSTNAME}/user_management/authorize?${params.toString()}`
  );
}

function readUrlUpgradeParam(): "success" | "cancelled" | null {
  const params = new URLSearchParams(window.location.search);
  const u = params.get("upgrade");
  if (u === "success" || u === "cancelled") return u;
  return null;
}

function clearUpgradeParamFromUrl() {
  const url = new URL(window.location.href);
  url.searchParams.delete("upgrade");
  window.history.replaceState({}, "", url.toString());
}

// Strip the AuthKit code (and any related query params) off the URL
// after the exchange so a page reload doesn't replay the now-spent
// code. Land on /dashboard. Keep the upgrade=success|cancelled params
// if present (Stripe redirects back through /dashboard?upgrade=...).
function clearAuthQueryAndPath() {
  const params = new URLSearchParams(window.location.search);
  params.delete("code");
  params.delete("state");
  const search = params.toString();
  const url = new URL(window.location.origin + sectionToPath("dashboard"));
  if (search.length > 0) url.search = `?${search}`;
  window.history.replaceState({}, "", url.toString());
}

function pushSection(section: DashboardSection) {
  const url = new URL(window.location.href);
  url.pathname = sectionToPath(section);
  // Drop the upgrade param when the user explicitly navigates; the
  // dismiss button handles that case separately.
  window.history.pushState({}, "", url.toString());
}

export function App() {
  const initialPath = window.location.pathname;
  const initialCode = new URLSearchParams(window.location.search).get("code");

  const [view, setView] = useState<View>(() => {
    if (initialPath === "/auth/callback" && initialCode) {
      return { kind: "exchanging" };
    }
    if (initialPath === "/login") {
      // /login is the AuthKit "Initiate Login URL" target. Stay on the
      // loading view; a mount effect kicks off sign-in (async, since the
      // PKCE setup hashes the verifier) and the hosted UI takes over.
      return { kind: "loading" };
    }
    return getSessionToken() ? { kind: "loading" } : { kind: "landing" };
  });

  // /login: start the AuthKit redirect. Kept in an effect rather than the
  // state initializer because beginAuth is async (PKCE challenge hashing).
  useEffect(() => {
    if (initialPath === "/login") {
      void beginAuth("sign-in");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync to browser back/forward.
  useEffect(() => {
    function onPopState() {
      setView((current) => {
        if (current.kind !== "dashboard") return current;
        const pathname = window.location.pathname;
        return {
          ...current,
          section: pathToSection(pathname),
          receiptDetailId: parseReceiptDetailId(pathname) ?? undefined,
          witnessLogDetailPosition: parseWitnessLogDetailPosition(pathname) ?? undefined
        };
      });
    }
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  // Exchange the AuthKit authorization code for a Sequesign session,
  // after validating the OAuth state against the value we stored before
  // redirecting. The PKCE code_verifier from the same stored transaction
  // is forwarded to the backend so WorkOS can confirm it hashes to the
  // challenge presented at the authorize step.
  useEffect(() => {
    if (view.kind !== "exchanging") return;

    // Read the code and state off the callback URL, then consume the
    // stored transaction. removeItem runs unconditionally so the
    // transaction is single-use: a replayed code finds nothing here.
    const callbackParams = new URLSearchParams(window.location.search);
    const code = callbackParams.get("code");
    const urlState = callbackParams.get("state");
    const rawTransaction = sessionStorage.getItem(AUTH_TXN_KEY);
    sessionStorage.removeItem(AUTH_TXN_KEY);

    const expiredReason =
      "Your sign-in session expired or didn't start in this tab. Please sign in again.";

    if (!code) {
      setView({ kind: "exchange_failed", reason: "Missing authorization code in callback URL." });
      return;
    }

    let transaction: { state?: unknown; code_verifier?: unknown } | null = null;
    if (rawTransaction) {
      try {
        transaction = JSON.parse(rawTransaction);
      } catch {
        transaction = null;
      }
    }
    if (
      !transaction ||
      typeof transaction.state !== "string" ||
      typeof transaction.code_verifier !== "string" ||
      !urlState
    ) {
      setView({ kind: "exchange_failed", reason: expiredReason });
      return;
    }
    if (!constantTimeEqual(urlState, transaction.state)) {
      setView({ kind: "exchange_failed", reason: expiredReason });
      return;
    }
    const codeVerifier = transaction.code_verifier;

    let cancelled = false;
    (async () => {
      try {
        const result = await api.authCallback({ code, code_verifier: codeVerifier });
        if (cancelled) return;
        setSessionToken(result.session_token);
        clearAuthQueryAndPath();
        landFromExchange(result);
      } catch (err) {
        if (cancelled) return;
        const reason =
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : "Sign-in failed.";
        clearAuthQueryAndPath();
        setView({ kind: "exchange_failed", reason });
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function landFromExchange(result: AuthCallbackResponse) {
    const pathname = window.location.pathname;
    setView({
      kind: "dashboard",
      email: result.customer.email,
      tier: result.customer.tier,
      section: pathToSection(pathname),
      receiptDetailId: parseReceiptDetailId(pathname) ?? undefined,
      witnessLogDetailPosition: parseWitnessLogDetailPosition(pathname) ?? undefined,
      issuedKeys: result.api_keys,
      upgradeNotice: readUrlUpgradeParam() ?? undefined
    });
  }

  // If we resumed with an existing Sequesign session, hydrate from /me.
  useEffect(() => {
    if (view.kind !== "loading") return;
    // /login (and /auth/callback) drive their own redirect/exchange
    // effects; do not flip them to landing while that runs.
    if (isAuthPath(window.location.pathname)) return;
    if (!getSessionToken()) {
      setView({ kind: "landing" });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const me = await api.me();
        if (cancelled) return;
        const pathname = window.location.pathname;
        setView({
          kind: "dashboard",
          email: me.customer.email,
          tier: me.customer.tier,
          section: pathToSection(pathname),
          receiptDetailId: parseReceiptDetailId(pathname) ?? undefined,
          witnessLogDetailPosition: parseWitnessLogDetailPosition(pathname) ?? undefined,
          upgradeNotice: readUrlUpgradeParam() ?? undefined
        });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          clearSessionToken();
          setView({ kind: "landing" });
          return;
        }
        setView({ kind: "landing" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [view.kind]);

  function goToSection(section: DashboardSection) {
    if (view.kind !== "dashboard") return;
    pushSection(section);
    // Leaving any nested detail view drops both ids from the view so
    // the sidebar always returns to the list view of the target
    // section.
    setView({
      ...view,
      section,
      receiptDetailId: undefined,
      witnessLogDetailPosition: undefined
    });
  }

  function goToReceiptDetail(receiptId: string) {
    if (view.kind !== "dashboard") return;
    const url = new URL(window.location.href);
    url.pathname = receiptDetailPath(receiptId);
    url.search = "";
    window.history.pushState({}, "", url.toString());
    setView({
      ...view,
      section: "receipts",
      receiptDetailId: receiptId,
      witnessLogDetailPosition: undefined
    });
  }

  function goToReceiptsList() {
    if (view.kind !== "dashboard") return;
    pushSection("receipts");
    setView({ ...view, section: "receipts", receiptDetailId: undefined });
  }

  function goToWitnessLogDetail(position: number) {
    if (view.kind !== "dashboard") return;
    const url = new URL(window.location.href);
    url.pathname = witnessLogDetailPath(position);
    url.search = "";
    window.history.pushState({}, "", url.toString());
    setView({
      ...view,
      section: "witness-log",
      witnessLogDetailPosition: position,
      receiptDetailId: undefined
    });
  }

  function goToWitnessLogList() {
    if (view.kind !== "dashboard") return;
    pushSection("witness-log");
    setView({ ...view, section: "witness-log", witnessLogDetailPosition: undefined });
  }

  function dismissUpgradeNotice() {
    if (view.kind !== "dashboard" || !view.upgradeNotice) return;
    clearUpgradeParamFromUrl();
    setView({ ...view, upgradeNotice: undefined });
  }

  async function handleSignOut() {
    // Call the server-side sign-out FIRST while the session token
    // is still valid. The endpoint marks the session row revoked,
    // calls WorkOS revokeSession to end the AuthKit session, and
    // returns the logout URL the browser should redirect to so
    // the AuthKit cookie clears too.
    //
    // If the network call fails we still clear local state and
    // land on the landing view; the user is signing out, and a
    // dashboard-api outage should not strand them on a signed-in
    // page. A surviving WorkOS cookie expires naturally.
    let logoutUrl: string | null = null;
    try {
      const response = await api.signOut();
      logoutUrl = response.logout_url;
    } catch {
      // dashboard-api unreachable or session already invalid; the
      // local cleanup below still happens.
    }
    clearSessionToken();
    // Drop any unsaved generated attestation key so it can't surface for the
    // next account that signs in on this tab (defense in depth; the pending
    // entry is also session-token-scoped on read).
    clearPendingRegistration();
    setView({ kind: "landing" });
    if (logoutUrl) {
      window.location.replace(logoutUrl);
    }
  }

  if (!WORKOS_CLIENT_ID) {
    return (
      <TooltipRoot>
        <div className="signup-shell">
          <Wordmark />
          <p className="muted">
            Dashboard is not configured. Build needs <code>VITE_WORKOS_CLIENT_ID</code> to enable
            sign-in.
          </p>
          <Footer />
        </div>
      </TooltipRoot>
    );
  }

  if (view.kind === "loading" || view.kind === "exchanging") {
    return (
      <TooltipRoot>
        <div className="signup-shell">
          <Wordmark />
          <p className="muted">{view.kind === "exchanging" ? "Completing sign-in…" : "Loading…"}</p>
          <Footer />
        </div>
      </TooltipRoot>
    );
  }

  if (view.kind === "exchange_failed") {
    return (
      <TooltipRoot>
        <div className="signup-shell">
          <Wordmark />
          <div className="panel" style={{ borderColor: "var(--failure)" }}>
            <h2 className="section-title">Sign-in failed</h2>
            <p>{view.reason}</p>
            <div style={{ marginTop: 16, display: "flex", gap: 12 }}>
              <button className="btn" onClick={() => void beginAuth("sign-in")}>
                Return to sign in
              </button>
              <button className="btn btn-secondary" onClick={() => setView({ kind: "landing" })}>
                Back to landing
              </button>
            </div>
          </div>
          <Footer />
        </div>
      </TooltipRoot>
    );
  }

  if (view.kind === "landing") {
    return (
      <TooltipRoot>
        <div className="signup-shell">
          <Wordmark />
          <p className="muted" style={{ marginBottom: 32 }}>
            Cryptographically verifiable receipts for delegated AI work.
          </p>
          <div className="panel">
            <h2 className="section-title">Get started</h2>
            <p>Create a Sequesign account or sign back in.</p>
            <div style={{ display: "flex", gap: 12, marginTop: 16 }}>
              <button className="btn" onClick={() => void beginAuth("sign-up")}>
                Sign up
              </button>
              <button className="btn btn-secondary" onClick={() => void beginAuth("sign-in")}>
                Sign in
              </button>
            </div>
            <p
              className="muted signup-terms"
              style={{ fontSize: 12, marginTop: 16, marginBottom: 0 }}
            >
              By signing up, you agree to our{" "}
              <a href="https://sequesign.com/terms">Terms of Service</a> and{" "}
              <a href="https://sequesign.com/privacy">Privacy Policy</a>.
            </p>
          </div>
          <p className="muted" style={{ marginTop: 24 }}>
            Need dedicated infrastructure or bring-your-own database? Contact sales.
          </p>
          <Footer />
        </div>
      </TooltipRoot>
    );
  }

  function renderSection() {
    if (view.kind !== "dashboard") return null;
    switch (view.section) {
      case "dashboard":
        return <DashboardPage onNavigate={(path) => goToSection(pathToSection(path))} />;
      case "usage":
        return <UsagePage />;
      case "keys":
        return <ApiKeysPage initialIssuedKeys={view.issuedKeys} />;
      case "registrations":
        return <RegistrationsPage />;
      case "templates":
        return <TemplatesPage />;
      case "receipts":
        if (view.receiptDetailId) {
          return <ReceiptDetailPage receiptId={view.receiptDetailId} onBack={goToReceiptsList} />;
        }
        return <ReceiptsPage onOpenDetail={goToReceiptDetail} />;
      case "witness-log":
        if (view.witnessLogDetailPosition !== undefined) {
          return (
            <WitnessLogDetailPage
              position={view.witnessLogDetailPosition}
              onBack={goToWitnessLogList}
            />
          );
        }
        return <WitnessLogPage onOpenDetail={goToWitnessLogDetail} />;
      case "billing":
        return <BillingPage onChangePlan={() => goToSection("upgrade")} />;
      case "settings":
        return <SettingsPage onSignOut={handleSignOut} />;
      case "upgrade":
        return (
          <UpgradePage currentTier={view.tier ?? "free"} onBack={() => goToSection("billing")} />
        );
      default:
        return null;
    }
  }

  return (
    <TooltipRoot>
      <DashboardLayout
        email={view.email}
        activeSection={view.section}
        onNavigate={goToSection}
        onSignOut={handleSignOut}
      >
        {view.upgradeNotice ? (
          <div
            className="panel"
            style={{
              borderColor: view.upgradeNotice === "success" ? "var(--verified)" : "var(--warning)"
            }}
          >
            {view.upgradeNotice === "success"
              ? "Upgrade complete. Stripe is processing the subscription; the new tier will appear here within a minute."
              : "Checkout was cancelled. You're still on your previous plan."}
            <div style={{ marginTop: 12 }}>
              <button className="btn btn-secondary" onClick={dismissUpgradeNotice}>
                Dismiss
              </button>
            </div>
          </div>
        ) : null}
        {renderSection()}
      </DashboardLayout>
    </TooltipRoot>
  );
}
